import { ImmutableArray, ImmutableObject } from "@hookstate/core"
import Router from "next/router"

import { SideNavItemProps } from "src/components/common/sideNav"
import { getUrlParameter } from "src/helpers/urlParams"
import { Category, getCategories } from "src/states/shop/category"
import { mergeFilter } from "src/states/shop/filter"

// URL parameters for linking to a category, built from the category names:
// /de/store-de/?category=elektroversorgung&sub=steckdosen
// The category ID is accepted as well (?category=<id>).
export const CATEGORY_PARAM = "category"
export const SUBCATEGORY_PARAM = "sub"

// Accepts the read-only values from the Hookstate store as well
type Categories = ReadonlyArray<Category> | ImmutableArray<Category>
type CategoryValue = Category | ImmutableObject<Category>

// Subcategories are typed as plain API objects and store values are
// read-only. Both are used as Category here, like elsewhere in the shop.
const asCategories = (items: unknown) => (items ?? []) as Category[]

const flattenCategories = (categories: Categories) => {
  const list = asCategories(categories)
  return [...list, ...list.flatMap((c) => asCategories(c.children))]
}

export const findCategory = (categories: Categories, id?: string | null) =>
  id ? flattenCategories(categories).find((c) => c.id === id) : undefined

// The first category is "Alle Produkte" (default selection)
export const getDefaultCategory = (categories: Categories) =>
  asCategories(categories)[0] as Category | undefined

export const getParentCategory = (
  categories: Categories,
  category?: CategoryValue | null,
) =>
  category?.categoryType === "subcategory"
    ? asCategories(categories).find((c) => c.id === category.parentCategoryId)
    : undefined

/** "Elektroversorgung & Licht" -> "elektroversorgung-licht" */
export const toSlug = (name?: string | null) =>
  (name ?? "")
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

// Falls back to the ID if a name has no usable characters
const getSlug = (category: CategoryValue) =>
  toSlug(category.name) || category.id

const findBySlug = (categories: unknown, slug?: string) =>
  slug ? asCategories(categories).find((c) => getSlug(c) === slug) : undefined

/** Resolves the category from the URL, undefined if it does not exist. */
export const getCategoryFromUrl = (categories: Categories) => {
  const categoryParam = getUrlParameter(CATEGORY_PARAM)
  const subParam = getUrlParameter(SUBCATEGORY_PARAM)

  const byId = findCategory(categories, categoryParam)
  if (byId) return byId

  const parent = findBySlug(categories, categoryParam)
  if (!parent || !subParam) return parent
  return findBySlug(parent.children, subParam) ?? parent
}

/**
 * Writes the selected category into the URL (without reloading), so the
 * current view can be linked and the back button restores it.
 */
export const updateCategoryParam = (category?: CategoryValue | null) => {
  if (typeof window === "undefined" || !Router.isReady) return

  const { categories } = getCategories()
  const isDefault =
    !category || category.id === getDefaultCategory(categories)?.id
  const parent = getParentCategory(categories, category)

  const params = new URLSearchParams(window.location.search)
  params.delete(CATEGORY_PARAM)
  params.delete(SUBCATEGORY_PARAM)
  if (!isDefault && parent) {
    params.set(CATEGORY_PARAM, getSlug(parent))
    params.set(SUBCATEGORY_PARAM, getSlug(category))
  } else if (!isDefault) {
    params.set(CATEGORY_PARAM, getSlug(category))
  }

  const search = params.toString() ? `?${params.toString()}` : ""
  if (search === window.location.search) return

  const query = { ...Router.query }
  delete query[CATEGORY_PARAM]
  delete query[SUBCATEGORY_PARAM]
  params.forEach((value, key) => {
    query[key] = value
  })

  Router.replace(
    { pathname: Router.pathname, query },
    `${window.location.pathname}${search}${window.location.hash}`,
    { shallow: true, scroll: false },
  )
}

/** Selecting a category always clears the search. */
export const selectCategory = (categoryId?: string) => {
  const { categories } = getCategories()
  const category =
    findCategory(categories, categoryId) ?? getDefaultCategory(categories)
  mergeFilter({ filterBy: { category: category ?? null }, search: "" })
  updateCategoryParam(category)
}

/** Searching always looks through all products. */
export const searchProducts = (search: string) => {
  if (!search) {
    mergeFilter({ search })
    return
  }
  const category = getDefaultCategory(getCategories().categories)
  mergeFilter({ search, filterBy: { category: category ?? null } })
  updateCategoryParam(category)
}

export const getCategoryNavItems = (
  categories: Categories,
  activeCategory: CategoryValue | null | undefined,
  onSelect: (category: Category) => void,
) =>
  asCategories(categories).map((category) => ({
    ...category,
    active:
      activeCategory?.id === category.id ||
      activeCategory?.parentCategoryId === category.id,
    onClick: () => onSelect(category),
    title: category.name,
    child: category.children?.map((subCategory) => ({
      ...subCategory,
      active: activeCategory?.id === subCategory.id,
      title: subCategory.name,
      onClick: () => onSelect(subCategory as Category),
    })),
  })) as ReadonlyArray<SideNavItemProps>
